import { useEffect, useState, type FormEvent } from "react";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { Modal } from "./Modal";
import { TextInput } from "./TextInput";
import { useSubmitFeedback } from "../lib/useSubmitFeedback";
import styles from "./FormModal.module.css";

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
  /** When provided, scopes the feedback to a specific spot — title
   *  becomes `Feedback on <spotName>`, the intro line tightens to the
   *  per-spot variant (Figma node 280:17387), and the payload carries
   *  `spotName` so the email subject identifies which spot the note is
   *  about. Omit for the generic footer trigger. */
  spotName?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Feedback modal — Figma node 273:10640 (left card).
 * Two required-ish fields (message is required, email is optional) and
 * a Send button that POSTs to `/api/feedback`. On success the body
 * swaps to a thank-you message and the action becomes "Close".
 */
export function FeedbackModal({ open, onClose, spotName }: FeedbackModalProps) {
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [honey, setHoney] = useState("");
  const [emailError, setEmailError] = useState(false);
  const { state, submit, reset } = useSubmitFeedback();

  // Wipe state every time the modal closes — re-opening with stale
  // content (or worse, a stuck "sent" view) would be confusing.
  useEffect(() => {
    if (!open) {
      setMessage("");
      setEmail("");
      setHoney("");
      setEmailError(false);
      reset();
    }
  }, [open, reset]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (email && !EMAIL_RE.test(email)) {
      setEmailError(true);
      return;
    }
    setEmailError(false);
    void submit({
      kind: "feedback",
      message: message.trim(),
      email: email.trim() || undefined,
      ...(spotName ? { spotName } : {}),
      // Honeypot — server silently 200s on any non-empty value.
      ...(honey ? { _honey: honey } : {}),
    } as Parameters<typeof submit>[0]);
  };

  const sent = state === "sent";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        sent
          ? "Thanks for the feedback"
          : spotName
            ? `Feedback on ${spotName}`
            : "Feedback"
      }
      description={
        sent
          ? "Got it, message landed in inbox. I read every one and reply when I can."
          : spotName
            ? "Think something's off? Tell me. Big Wind gets better when the people using it say what's missing."
            : "Found a bug, got an idea, or think something's off? Tell me. Big Wind gets better when the people using it say what's missing."
      }
    >
      {sent ? (
        <div className={styles.actions}>
          <hr className={styles.divider} />
          <Button variant="elevated" onClick={onClose}>
            Close
          </Button>
        </div>
      ) : (
        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <div className={styles.fields}>
            <TextInput
              multiline
              label="Message*"
              rows={4}
              required
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What's on your mind?"
              disabled={state === "sending"}
            />
            <TextInput
              type="email"
              autoComplete="email"
              inputMode="email"
              label="Your email (optional)"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(false);
              }}
              error={emailError}
              hint={
                emailError
                  ? "Doesn't look like a valid email."
                  : "Only if you'd like a reply."
              }
              disabled={state === "sending"}
            />
            {/* Honeypot — visually and AT-hidden. Real users skip it,
                bots fill it. Server silently 200s if it's populated. */}
            <label className={styles.honeypot} aria-hidden tabIndex={-1}>
              Don't fill this in
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honey}
                onChange={(e) => setHoney(e.target.value)}
              />
            </label>
            {state === "error" && (
              <p className={styles.errorBanner} role="alert">
                Couldn't send. Check your connection and try again, or
                email info@bigwind.surf directly.
              </p>
            )}
          </div>
          <div className={styles.actions}>
            <hr className={styles.divider} />
            <Button
              type="submit"
              variant="elevated"
              disabled={state === "sending" || message.trim().length === 0}
            >
              <Icon name="send" />
              {state === "sending" ? "Sending…" : "Send"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
